/**
 * MARCA | 02/06/2026 | Quick filter pills custom element.
 * Syncs togglable pill buttons with existing Search & Discovery filter checkboxes.
 * Clicking a pill toggles the matching checkbox in the facets form and triggers
 * the standard section re-render via the existing FacetsFormComponent.
 */

class CollectionFilterPills extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", this.#handleClick);
    this.#positionAfterControlsBar();
    this.#updateFade();
    this.#track?.addEventListener("scroll", this.#updateFade);
    if (typeof ResizeObserver !== "undefined") {
      this.#resizeObserver = new ResizeObserver(this.#updateFade);
      const trackEl = this.#track;
      if (trackEl) this.#resizeObserver.observe(trackEl);
    }
  }

  #positionAfterControlsBar() {
    const container = this.#getSectionContainer();
    const controlsBar = container.querySelector(".facets-controls-wrapper");
    if (controlsBar && controlsBar.parentNode) {
      controlsBar.parentNode.insertBefore(this, controlsBar.nextSibling);
    }
  }

  #updateFade = () => {
    const track = this.#track;
    if (!track) return;
    const atEnd = track.scrollWidth - track.scrollLeft - track.clientWidth < 2;
    this.classList.toggle("collection-filter-pills--no-fade", atEnd);
  };

  /** @type {ResizeObserver|null} */
  #resizeObserver = null;

  get #track() {
    return this.querySelector(".collection-filter-pills__track");
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.#handleClick);
    this.#track?.removeEventListener("scroll", this.#updateFade);
    if (this.#resizeObserver) this.#resizeObserver.disconnect();
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const pill = target.closest(".collection-filter-pills__pill");
    const clearBtn = target.closest(".collection-filter-pills__clear");

    if (pill) {
      this.#togglePillFilter(/** @type {HTMLElement} */ (pill));
    } else if (clearBtn) {
      this.#clearAllPills();
    }
  };

  /** @returns {Element} */
  #getSectionContainer() {
    return this.closest(".shopify-section") || document.documentElement;
  }

  #getFacetsForm() {
    const container = this.#getSectionContainer();
    return container.querySelector("facets-form-component");
  }

  /** @param {HTMLElement} pill */
  #togglePillFilter(pill) {
    const param = pill.dataset.filterParam;
    const value = pill.dataset.filterValue;
    if (!param || !value) return;

    const container = this.#getSectionContainer();
    const checkboxes = /** @type {NodeListOf<HTMLInputElement>} */ (
      container.querySelectorAll(
        `input[type="checkbox"][name="${CSS.escape(param)}"][value="${CSS.escape(value)}"]`,
      )
    );

    if (checkboxes.length === 0) return;

    const shouldActivate = !pill.classList.contains(
      "collection-filter-pills__pill--active",
    );

    checkboxes.forEach((checkbox) => {
      checkbox.checked = shouldActivate;
    });

    const facetsForm = this.#getFacetsForm();
    if (facetsForm && "updateFilters" in facetsForm) {
      /** @type {any} */ (facetsForm).updateFilters();
    }
  }

  #clearAllPills() {
    const container = this.#getSectionContainer();
    const activePills = this.querySelectorAll(
      ".collection-filter-pills__pill--active",
    );

    activePills.forEach((pillEl) => {
      const pill = /** @type {HTMLElement} */ (pillEl);
      const param = pill.dataset.filterParam;
      const value = pill.dataset.filterValue;

      if (!param || !value) return;

      const checkboxes = /** @type {NodeListOf<HTMLInputElement>} */ (
        container.querySelectorAll(
          `input[type="checkbox"][name="${CSS.escape(param)}"][value="${CSS.escape(value)}"]`,
        )
      );

      checkboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });
    });

    const facetsForm = this.#getFacetsForm();
    if (facetsForm && "updateFilters" in facetsForm) {
      /** @type {any} */ (facetsForm).updateFilters();
    }
  }
}

if (!customElements.get('collection-filter-pills')) {
  customElements.define('collection-filter-pills', CollectionFilterPills);
}
